import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createSubscriptionPlan,
  ERROR_MESSAGES,
  type CreateSubscriptionPlanInput,
} from '@/epics/subscriptionManagment/api';
import { SUBSCRIPTION_ERROR_MESSAGES } from '@/epics/subscriptionManagment/errors';
import type {
  ServiceConfigEntry,
  ServiceConfigFieldErrors,
} from '@/epics/subscriptionManagment/components/ServiceConfigCard';
import {
  listEquipmentCategories,
  type EquipmentCategoryItem,
} from '@/epics/facilityManagement/api/listEquipmentCategoriesApiService';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '@/epics/subscriptionManagment/constants/translations/labels';
import {
  computeFinalPrice,
  computeOfferFinalPrice,
  getCycleSuffix,
} from '@/epics/subscriptionManagment/utils/pricing';
import type { PlanPreviewData } from '@/epics/subscriptionManagment/components/PlanPreviewAccordion';

// ============================================================================
// Constants
// ============================================================================

// Collapses leading zeros in the integer part of a discount-percentage value
// (e.g. "0039.30" -> "39.30", "000" -> "0") so a stored value is always
// normalized regardless of which caller set it — typing, paste, or an
// initial load — not just the one sanitizing the keystroke.
export function stripLeadingZeros(value: string): string {
  const [intPart = '', decPart] = value.split('.');
  const normalizedIntPart = intPart.replace(/^0+(?=\d)/, '');
  return decPart !== undefined
    ? `${normalizedIntPart}.${decPart}`
    : normalizedIntPart;
}

// Shared with the Edit wizard.
export const STEP_TABS = [
  { label: SUBSCRIPTION_MANAGEMENT_LABELS.STEP_PLAN_DETAILS.polish },
  { label: SUBSCRIPTION_MANAGEMENT_LABELS.STEP_SERVICES.polish },
  { label: SUBSCRIPTION_MANAGEMENT_LABELS.STEP_EQUIPMENT_PRICING.polish },
  { label: SUBSCRIPTION_MANAGEMENT_LABELS.STEP_DISCOUNT_LOGIC.polish },
];

export const PERIOD_TYPE_OPTIONS = [
  { id: 'Miesięczny', label: 'Miesięczny' },
  { id: 'Roczny', label: 'Roczny' },
];

export const PERIOD_OPTIONS_MONTHLY = [
  { id: '1 miesiąc', label: '1 miesiąc' },
  { id: '2 miesiące', label: '2 miesiące' },
  { id: '3 miesiące', label: '3 miesiące' },
  { id: '4 miesiące', label: '4 miesiące' },
  { id: '5 miesiące', label: '5 miesiące' },
  { id: '6 miesiące', label: '6 miesiące' },
  { id: '7 miesiące', label: '7 miesiące' },
  { id: '8 miesiące', label: '8 miesiące' },
  { id: '9 miesiące', label: '9 miesiące' },
  { id: '10 miesiące', label: '10 miesiące' },
  { id: '11 miesiące', label: '11 miesiące' },
  { id: '12 miesiące', label: '12 miesiące' },
];

export const PERIOD_OPTIONS_YEARLY = [
  { id: '1 rok', label: '1 rok' },
  { id: '2 lata', label: '2 lata' },
  { id: '3 lata', label: '3 lata' },
  { id: '4 lata', label: '4 lata' },
  { id: '5 lat', label: '5 lat' },
  { id: '6 lat', label: '6 lat' },
  { id: '7 lat', label: '7 lat' },
  { id: '8 lat', label: '8 lat' },
  { id: '9 lat', label: '9 lat' },
  { id: '10 lat', label: '10 lat' },
];

const CYCLE_OPTIONS_MONTHLY = [{ id: 'Miesięczny', label: 'Miesięczny' }];
const CYCLE_OPTIONS_QUARTERLY = [
  { id: 'Miesięczny', label: 'Miesięczny' },
  { id: 'kwartalny', label: 'Kwartalny' },
];

const CYCLE_OPTIONS_YEARLY = [
  { id: 'Miesięczny', label: 'Miesięczny' },
  { id: 'kwartalny', label: 'Kwartalny' },
  { id: 'Roczny', label: 'Roczny' },
];

// ============================================================================
// Types
// ============================================================================

export interface CreateSubscriptionFormData {
  planName: string;
  description: string;
  price: string;
  userCount: string;
  facilityCount: string;
  qmsDocCount: string;
}

export type FormErrors = {
  planName?: string;
  description?: string;
  durationType?: string;
  period?: string;
  cycle?: string;
  price?: string;
  userCount?: string;
  facilityCount?: string;
};

export interface OfferDiscountInfo {
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: number;
}

export type CreateSubscriptionFormProps = {
  formData: CreateSubscriptionFormData;
  setFormData: React.Dispatch<React.SetStateAction<CreateSubscriptionFormData>>;
  durationType: string;
  setDurationType: (id: string) => void;
  period: string;
  setPeriod: (id: string) => void;
  cycle: string;
  setCycle: (id: string) => void;
  cycleOptions: { id: string; label: string }[];
  errors: FormErrors;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  offerId: string;
  setOfferId: (id: string) => void;
  offerName: string;
  setOfferName: (name: string) => void;
  offerDiscount: OfferDiscountInfo | null;
  setOfferDiscount: (discount: OfferDiscountInfo | null) => void;
  formatPriceWithSpaces: (price: string) => string;
  getBillingDescription: (
    price: string,
    cycle: string,
    period: string,
    formatPriceWithSpacesFn: (p: string) => string
  ) => string;
  isSubmitting: boolean;
};

// ============================================================================
// Initial state
// ============================================================================

export const INITIAL_FORM_DATA: CreateSubscriptionFormData = {
  planName: '',
  description: '',
  price: '',
  userCount: '',
  facilityCount: '',
  qmsDocCount: '',
};

// Shared ceiling for every money field (plan price, service/equipment base
// price) — mirrors pozytron-backend's MAX_PRICE (services/subscription_management/
// src/types/subscription.types.ts): 9,999,999,999,999.99, a DECIMAL(15,2)-
// equivalent value. isPriceTooBig checks this as an integer-digit count
// rather than a numeric comparison against this constant (see below) — this
// value is kept around for display/tests and as the documented reference.
export const MAX_PRICE = 9_999_999_999_999.99;

// The raw value at MAX_PRICE is 13 integer digits + "." + 2 decimals = 16
// characters — but basePrice fields display via formatPriceWithSpaces
// (thousand-separator spaces + comma decimal, e.g. "9 999 999 999 999,99"),
// and maxLength constrains what's actually typed into the input, not the
// underlying raw value. A 13-digit integer group into 5 groups (1+3+3+3+3),
// adding 4 separator spaces: 16 + 4 = 20. A flat 16 here would truncate
// before the user could ever type the real maximum.
export const MAX_PRICE_LENGTH = 20;

// MAX_PRICE's own integer part is 13 nines — the largest possible 13-digit
// number — so "more than 13 integer digits" and "greater than MAX_PRICE"
// are exactly equivalent for the integer part. Checked as a digit count
// rather than a numeric comparison: it's exact regardless of magnitude
// (no float precision concern for a pathologically long paste), and it's
// the same rule maxLength enforces, just not bypassable by pasting.
const MAX_PRICE_INTEGER_DIGITS = 13;

/** True once the value has more integer digits than MAX_PRICE allows. */
export function isPriceTooBig(value: string): boolean {
  const normalized = value.replace(/\s/g, '').replace(',', '.');
  const [integerPart = ''] = normalized.split('.');
  const digitsOnly = integerPart.replace(/\D/g, '');
  const significantDigits = digitsOnly.replace(/^0+(?=\d)/, '');
  return significantDigits.length > MAX_PRICE_INTEGER_DIGITS;
}

// Discount tiers are keyed by equipment price rank (1-7); rank 7 is the
// ceiling and also covers any category ranked 8th or beyond.
export const EQUIPMENT_RANK_COUNT = 7;

export const DEFAULT_DISCOUNT_TIERS: Record<number, string> = {
  1: '0',
  2: '10',
  3: '20',
  4: '20',
  5: '20',
  6: '20',
  7: '20',
};

// ============================================================================
// Helper functions
// ============================================================================

export function getCycleOptions(
  durationType: string,
  period: string
): { id: string; label: string }[] {
  if (durationType === 'Roczny') {
    return CYCLE_OPTIONS_YEARLY;
  }
  if (durationType === 'Miesięczny') {
    if (
      period === '1 miesiąc' ||
      period === '2 miesiące' ||
      period === '3 miesiące' ||
      period === '4 miesiące' ||
      period === '5 miesiące'
    ) {
      return CYCLE_OPTIONS_MONTHLY;
    }
    if (period === '6 miesiące') {
      return CYCLE_OPTIONS_QUARTERLY;
    }
    if (period === '12 miesiące') {
      return CYCLE_OPTIONS_YEARLY;
    }
    return CYCLE_OPTIONS_QUARTERLY;
  }
  return [];
}

/**
 * Format price with space as thousand separator and comma as decimal:
 * 1234567.89 → "1 234 567,89". Backfills a leading zero when the integer
 * part is empty (".30" → "0,30") — otherwise deleting a leading "0" would
 * leave a decimal point with nothing in front of it.
 */
export function formatPriceWithSpaces(price: string): string {
  if (!price) {
    return price;
  }
  const stripped = price.replace(/\s/g, '');
  const [intPart = '', decPart] = stripped.split('.');
  const strippedIntPart = intPart.replace(/^0+/, '') || '0';
  const formattedInt = strippedIntPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
}

/** Map billing cycle id to English label for the billing sentence */
export function cycleToEnglish(cycle: string): string {
  switch (cycle) {
    case 'Miesięczny':
      return 'monthly';
    case 'kwartalny':
      return 'quarterly';
    case 'Roczny':
      return 'yearly';
    default:
      return cycle;
  }
}

/** Map billing cycle id to Polish label for the billing sentence (e.g. "co miesiąc", "co roku") */
function cycleToPolish(cycle: string): string {
  switch (cycle) {
    case 'Miesięczny':
      return 'co miesiąc';
    case 'kwartalny':
      return 'co kwartał';
    case 'Roczny':
      return 'co roku';
    default:
      return cycle;
  }
}

/** Map period id to English "X month(s)" or "X year(s)" for the billing sentence */
export function periodToEnglish(period: string): string {
  if (period === '1 miesiąc') {
    return '1 month';
  }
  if (/^\d+ miesiące$/.test(period)) {
    const n = period.replace(/\s*miesiące$/, '');
    return `${n} months`;
  }
  if (period === '1 rok') {
    return '1 year';
  }
  if (/^\d+ lata$/.test(period)) {
    const n = period.replace(/\s*lata$/, '');
    return `${n} years`;
  }
  if (/^\d+ lat$/.test(period)) {
    const n = period.replace(/\s*lat$/, '');
    return `${n} years`;
  }
  return period;
}

/** Build billing description sentence in Polish (e.g. "Za 1 miesiąc zostanie naliczona opłata 1 499,00 zł." or "Przez 2 lata opłata 1 499,00 zł będzie naliczana co roku.") */
export function getBillingDescription(
  price: string,
  cycle: string,
  period: string,
  formatPriceWithSpacesFn: (p: string) => string
): string {
  const hasPrice = price != null && price.replace(/\s/g, '').length > 0;
  const hasCycle = cycle != null && cycle.length > 0;
  const hasPeriod = period != null && period.length > 0;

  if (hasPrice && hasCycle && hasPeriod) {
    let formatted =
      Number(price) > 0
        ? formatPriceWithSpacesFn(price).replace('.', ',')
        : '0,00';
    if (!formatted.includes(',')) {
      formatted = `${formatted},00`;
    }
    const cyclePl = cycleToPolish(cycle);

    if (period === '1 miesiąc') {
      return `Za 1 miesiąc zostanie naliczona opłata ${formatted} zł.`;
    }
    return `Przez ${period} opłata ${formatted} zł będzie naliczana ${cyclePl}.`;
  }

  return SUBSCRIPTION_MANAGEMENT_LABELS.INFO_BILLING_DESCRIPTION_DEFAULT.polish;
}

/**
 * A service entry counts toward submission once a service is picked and
 * base price is filled. Usage Limit is only required when a discount is
 * applied — with no discount there's nothing for it to gate, so it's left
 * blank and disabled in the UI.
 */
export function isServiceEntryComplete(entry: ServiceConfigEntry): boolean {
  const hasDiscount = entry.discountPercentage.trim() !== '';
  return (
    entry.serviceTypeId.trim() !== '' &&
    entry.basePrice.trim() !== '' &&
    (!hasDiscount ||
      (entry.usageLimit.trim() !== '' && Number(entry.usageLimit) >= 1))
  );
}

/** A blank, freshly-added entry can be discarded outright; anything the user has started filling in gets a confirmation prompt first. */
export function isServiceEntryEmpty(entry: ServiceConfigEntry): boolean {
  return (
    entry.serviceTypeId.trim() === '' &&
    entry.usageLimit.trim() === '' &&
    entry.basePrice.trim() === '' &&
    entry.discountPercentage.trim() === ''
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useCreateSubscription() {
  const navigate = useNavigate();

  // Wizard step — 1: Plan Details, 2: Services, 3: Equipment Pricing, 4: Discount Logic.
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  // Furthest step successfully validated so far — drives which tabs are
  // clickable and which show a completed checkmark. Only advances when a
  // step's validation passes; going back and forth below it needs no
  // re-validation.
  const [maxReachedStep, setMaxReachedStep] = useState<1 | 2 | 3 | 4>(1);

  // Tick marks are event-driven here (ADD flow): a step only ticks once its
  // fields have validated AND the user has moved past it via Next — never
  // just because its data happens to be valid. Sticky once earned, since the
  // only way to move forward always re-validates (see handleSubmit/
  // handleStepTabClick), so a stale tick can never show against bad data.
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(
    () => new Set()
  );

  // Step 1 form state
  const [durationType, setDurationType] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [cycle, setCycle] = useState<string>('');
  const [formData, setFormData] =
    useState<CreateSubscriptionFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [offerId, setOfferId] = useState<string>('');
  const [offerDiscount, setOfferDiscount] = useState<OfferDiscountInfo | null>(
    null
  );
  // Only used to display the offer's name on the preview screen — the
  // dropdown itself resolves offerId to a display value independently.
  const [offerName, setOfferName] = useState<string>('');

  // Step 2 (Services) state
  const [services, setServices] = useState<ServiceConfigEntry[]>([]);
  const [expandedServiceKey, setExpandedServiceKey] = useState<string | null>(
    null
  );
  const [serviceFieldErrors, setServiceFieldErrors] = useState<
    Record<string, ServiceConfigFieldErrors>
  >({});
  const [servicesError, setServicesError] = useState('');
  const [removeDialogKey, setRemoveDialogKey] = useState<string | null>(null);

  // Step 3 (Equipment Pricing) state
  const [equipmentCategoryOptions, setEquipmentCategoryOptions] = useState<
    EquipmentCategoryItem[]
  >([]);
  const [equipmentCategoriesLoading, setEquipmentCategoriesLoading] =
    useState(false);
  const [equipmentPrices, setEquipmentPrices] = useState<
    Record<string, string>
  >({});
  const [equipmentPriceErrors, setEquipmentPriceErrors] = useState<
    Record<string, string>
  >({});

  // Step 4 (Discount Logic) state
  const [discountTiers, setDiscountTiers] = useState<Record<number, string>>(
    () => ({ ...DEFAULT_DISCOUNT_TIERS })
  );
  const [discountTierErrors, setDiscountTierErrors] = useState<
    Record<number, string>
  >({});

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  // Step 4's own submit enters this preview stage instead of calling the API
  // directly — mirrors Edit's existing two-click step pattern.
  const [isPreviewStep, setIsPreviewStep] = useState(false);

  // Derived
  const cycleOptions = getCycleOptions(durationType, period);
  const removeDialogEntry =
    services.find((s) => s.key === removeDialogKey) ?? null;
  // Block adding another service while any existing one is still missing a
  // required field — otherwise incomplete cards could pile up unbounded.
  const hasIncompleteService = services.some(
    (entry) => !isServiceEntryComplete(entry)
  );

  // Plain, pre-formatted data for the read-only preview screen — kept
  // decoupled from PlanPreviewAccordion's own internals.
  const previewData: PlanPreviewData = useMemo(() => {
    const priceNum =
      parseFloat(
        (formData.price || '0').replace(/\s/g, '').replace(',', '.')
      ) || 0;
    const finalPriceNum = computeOfferFinalPrice(priceNum, offerDiscount);
    const cycleSuffix = getCycleSuffix(cycleToEnglish(cycle));

    return {
      planDetails: {
        planName: formData.planName,
        durationType,
        period,
        billingCycle: cycleOptions.find((o) => o.id === cycle)?.label ?? cycle,
        description: formData.description,
        price: `${formatPriceWithSpaces(formData.price)} zł`,
        userCount: formData.userCount,
        facilityCount: formData.facilityCount,
        qmsDocCount: formData.qmsDocCount,
        offer:
          offerId && offerName
            ? {
                name: offerName,
                discountLabel:
                  offerDiscount?.discountType === 'PERCENTAGE'
                    ? `${offerDiscount.discountValue}% off`
                    : offerDiscount
                      ? `${formatPriceWithSpaces(offerDiscount.discountValue.toFixed(2))} zł off`
                      : '',
              }
            : null,
        finalPrice: `${formatPriceWithSpaces(finalPriceNum.toFixed(2))} zł${cycleSuffix}`,
      },
      services: services.map((entry) => {
        const basePriceNum = parseFloat(entry.basePrice) || 0;
        const discountNum = entry.discountPercentage
          ? parseFloat(entry.discountPercentage)
          : null;
        return {
          key: entry.key,
          serviceName: entry.serviceName || '—',
          usageLimit: entry.usageLimit || '—',
          basePrice: `${formatPriceWithSpaces(entry.basePrice || '0')} zł`,
          discountLabel: discountNum ? `${discountNum}%` : '0%',
          finalPrice: `${formatPriceWithSpaces(computeFinalPrice(basePriceNum, discountNum).toFixed(2))} zł`,
        };
      }),
      equipmentCategories: equipmentCategoryOptions.map((category) => ({
        id: category.id,
        categoryLabel: category.label,
        basePrice: `${formatPriceWithSpaces(equipmentPrices[category.id] || '0')} zł`,
      })),
      discountTiers: Array.from({ length: EQUIPMENT_RANK_COUNT }, (_, i) => ({
        rank: i + 1,
        discountLabel: `${discountTiers[i + 1] ?? '0'}%`,
      })),
    };
  }, [
    formData,
    durationType,
    period,
    cycle,
    cycleOptions,
    offerId,
    offerName,
    offerDiscount,
    services,
    equipmentCategoryOptions,
    equipmentPrices,
    discountTiers,
  ]);

  // Reset period and cycle when period type changes (options change)
  useEffect(() => {
    setPeriod('');
    setCycle('');
  }, [durationType]);

  // Reset cycle when period changes (cycle options may change)
  useEffect(() => {
    setCycle('');
  }, [period]);

  // Auto-fill cycle when there is only one option
  useEffect(() => {
    const options = getCycleOptions(durationType, period);
    const singleOption = options[0];
    if (options.length === 1 && singleOption) {
      setErrors((prev) => ({ ...prev, cycle: '' }));
      setCycle(singleOption.id);
    }
  }, [durationType, period]);

  // The equipment category list is a small, always-shown catalog — fetched
  // once up front rather than lazily per-step, same as the static period/cycle
  // options above. Only active categories are offered, matching the set the
  // backend validates equipmentCategories against.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setEquipmentCategoriesLoading(true);
      try {
        const result = await listEquipmentCategories({
          page: 1,
          limit: 100,
          status: 'active',
        });
        if (!cancelled) {
          setEquipmentCategoryOptions(result.data);
        }
      } finally {
        if (!cancelled) {
          setEquipmentCategoriesLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // --------------------------------------------------------------------------
  // Services (step 2) handlers
  // --------------------------------------------------------------------------

  const handleAddService = useCallback(() => {
    const key = crypto.randomUUID();
    setServices((prev) => [
      ...prev,
      {
        key,
        serviceTypeId: '',
        serviceName: '',
        usageLimit: '',
        basePrice: '',
        discountPercentage: '',
      },
    ]);
    setExpandedServiceKey(key);
    setServicesError('');
  }, []);

  // Step 2 never starts empty — one blank card is open by
  // default so the user isn't required to click "Add Service" first.
  useEffect(() => {
    if (step === 2 && services.length === 0) {
      handleAddService();
    }
  }, [step, services.length, handleAddService]);

  const handleServiceTypeChange = useCallback(
    (key: string, serviceTypeId: string, serviceName: string) => {
      setServices((prev) =>
        prev.map((entry) =>
          entry.key === key ? { ...entry, serviceTypeId, serviceName } : entry
        )
      );
      setServiceFieldErrors((prev) => {
        if (!prev[key]?.serviceTypeId) {
          return prev;
        }
        return { ...prev, [key]: { ...prev[key], serviceTypeId: '' } };
      });
    },
    []
  );

  const handleServiceFieldChange = useCallback(
    (
      key: string,
      field: 'usageLimit' | 'basePrice' | 'discountPercentage',
      value: string
    ) => {
      const nextValue =
        field === 'discountPercentage' ? stripLeadingZeros(value) : value;
      setServices((prev) =>
        prev.map((entry) =>
          entry.key === key ? { ...entry, [field]: nextValue } : entry
        )
      );
      setServiceFieldErrors((prev) => {
        if (!prev[key]) {
          return prev;
        }
        return { ...prev, [key]: { ...prev[key], [field]: '' } };
      });
    },
    []
  );

  // Only one service card can be expanded at a time — a single piece of
  // state (rather than a per-card boolean) enforces that by construction.
  const handleToggleExpandService = useCallback((key: string) => {
    setExpandedServiceKey((prev) => (prev === key ? null : key));
  }, []);

  const handleRequestRemoveService = useCallback(
    (key: string) => {
      const entry = services.find((s) => s.key === key);
      if (!entry) {
        return;
      }
      // A blank card the user hasn't started filling in can be discarded
      // outright — the confirmation prompt only applies once there's
      // something to lose.
      if (isServiceEntryEmpty(entry)) {
        setServices((prev) => prev.filter((s) => s.key !== key));
        setExpandedServiceKey((prev) => (prev === key ? null : prev));
      } else {
        setRemoveDialogKey(key);
      }
    },
    [services]
  );

  const handleCloseRemoveDialog = useCallback(() => {
    setRemoveDialogKey(null);
  }, []);

  const handleConfirmRemoveService = useCallback(() => {
    setServices((prev) => prev.filter((s) => s.key !== removeDialogKey));
    setExpandedServiceKey((prev) => (prev === removeDialogKey ? null : prev));
    setRemoveDialogKey(null);
  }, [removeDialogKey]);

  // --------------------------------------------------------------------------
  // Equipment Pricing (step 3) / Discount Logic (step 4) handlers
  // --------------------------------------------------------------------------

  const handleEquipmentPriceChange = useCallback(
    (categoryId: string, value: string) => {
      setEquipmentPrices((prev) => ({
        ...prev,
        [categoryId]: value,
      }));
      setEquipmentPriceErrors((prev) => {
        if (!prev[categoryId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[categoryId];
        return next;
      });
    },
    []
  );

  const handleDiscountTierChange = useCallback(
    (rank: number, value: string) => {
      setDiscountTiers((prev) => ({
        ...prev,
        [rank]: stripLeadingZeros(value),
      }));
      setDiscountTierErrors((prev) => {
        if (!prev[rank]) {
          return prev;
        }
        const next = { ...prev };
        delete next[rank];
        return next;
      });
    },
    []
  );

  // --------------------------------------------------------------------------
  // Submit / step navigation
  // --------------------------------------------------------------------------

  const validateStep1 = useCallback((): FormErrors => {
    const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
    const newErrors: FormErrors = {};

    if (!formData.planName.trim()) {
      newErrors.planName = requiredMessage;
    }
    if (!formData.description.trim()) {
      newErrors.description = requiredMessage;
    }
    if (!durationType) {
      newErrors.durationType = requiredMessage;
    }
    if (!period) {
      newErrors.period = requiredMessage;
    }
    if (!cycle) {
      newErrors.cycle = requiredMessage;
    }
    const priceNum = parseFloat(formData.price.replace(/\s/g, ''));
    if (
      formData.price.trim() === '' ||
      Number.isNaN(priceNum) ||
      priceNum < 0
    ) {
      newErrors.price = requiredMessage;
    } else if (isPriceTooBig(formData.price)) {
      newErrors.price = SUBSCRIPTION_ERROR_MESSAGES.PRICE_TOO_BIG;
    }
    if (!formData.userCount.trim()) {
      newErrors.userCount = requiredMessage;
    }
    if (!formData.facilityCount.trim()) {
      newErrors.facilityCount = requiredMessage;
    }

    return newErrors;
  }, [formData, durationType, period, cycle]);

  const validateStep2Services = useCallback((): {
    fieldErrors: Record<string, ServiceConfigFieldErrors>;
    firstIncompleteKey: string | null;
    generalError: string;
  } => {
    const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
    const nextFieldErrors: Record<string, ServiceConfigFieldErrors> = {};
    let firstIncompleteKey: string | null = null;

    for (const entry of services) {
      const entryErrors: ServiceConfigFieldErrors = {};
      if (!entry.serviceTypeId.trim()) {
        entryErrors.serviceTypeId = requiredMessage;
      }
      // Usage Limit only matters when a discount is applied — it's the
      // count of times the discounted price applies before the base price
      // takes over, so with no discount there's nothing for it to gate.
      if (
        entry.discountPercentage.trim() !== '' &&
        (!entry.usageLimit.trim() || Number(entry.usageLimit) < 1)
      ) {
        entryErrors.usageLimit = requiredMessage;
      }
      if (!entry.basePrice.trim()) {
        entryErrors.basePrice = requiredMessage;
      } else if (isPriceTooBig(entry.basePrice)) {
        // Not clamped on input (see ServiceConfigCard) — an oversized value
        // must surface as an error here instead of being silently rewritten.
        entryErrors.basePrice = SUBSCRIPTION_ERROR_MESSAGES.INVALID_BASE_PRICE;
      }
      if (Object.keys(entryErrors).length > 0) {
        nextFieldErrors[entry.key] = entryErrors;
        firstIncompleteKey ??= entry.key;
      }
    }

    if (firstIncompleteKey) {
      return {
        fieldErrors: nextFieldErrors,
        firstIncompleteKey,
        generalError: '',
      };
    }
    if (services.length === 0) {
      return {
        fieldErrors: {},
        firstIncompleteKey: null,
        generalError: 'At least one service must be selected',
      };
    }
    return { fieldErrors: {}, firstIncompleteKey: null, generalError: '' };
  }, [services]);

  const validateStep3EquipmentPrices = useCallback((): Record<
    string,
    string
  > => {
    const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
    const newErrors: Record<string, string> = {};
    for (const category of equipmentCategoryOptions) {
      const raw = equipmentPrices[category.id] ?? '';
      const priceNum = parseFloat(raw.replace(',', '.'));
      if (raw.trim() === '' || Number.isNaN(priceNum) || priceNum < 0) {
        newErrors[category.id] = requiredMessage;
      } else if (isPriceTooBig(raw)) {
        newErrors[category.id] = SUBSCRIPTION_ERROR_MESSAGES.INVALID_BASE_PRICE;
      }
    }
    return newErrors;
  }, [equipmentCategoryOptions, equipmentPrices]);

  const validateStep4DiscountTiers = useCallback((): Record<number, string> => {
    const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
    const newErrors: Record<number, string> = {};
    for (let rank = 1; rank <= EQUIPMENT_RANK_COUNT; rank += 1) {
      const raw = discountTiers[rank] ?? '';
      const pctNum = Number(raw);
      if (
        raw.trim() === '' ||
        Number.isNaN(pctNum) ||
        pctNum < 0 ||
        pctNum > 100
      ) {
        newErrors[rank] = requiredMessage;
      }
    }
    return newErrors;
  }, [discountTiers]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Step 1 "Next": validate and advance to Services.
      if (step === 1) {
        const newErrors = validateStep1();
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
          return;
        }
        setCompletedSteps((prev) => new Set(prev).add(1));
        setStep(2);
        setMaxReachedStep((prev) => Math.max(prev, 2) as 1 | 2 | 3 | 4);
        return;
      }

      // Step 2 "Next": validate services, then advance to Equipment Pricing.
      if (step === 2) {
        const { fieldErrors, firstIncompleteKey, generalError } =
          validateStep2Services();
        if (firstIncompleteKey) {
          setServiceFieldErrors(fieldErrors);
          setExpandedServiceKey(firstIncompleteKey);
          setServicesError('');
          return;
        }
        if (generalError) {
          setServicesError(generalError);
          return;
        }
        setServicesError('');
        setCompletedSteps((prev) => new Set(prev).add(2));
        setStep(3);
        setMaxReachedStep((prev) => Math.max(prev, 3) as 1 | 2 | 3 | 4);
        return;
      }

      // Step 3 "Next": validate equipment category prices, then advance to
      // Discount Logic.
      if (step === 3) {
        const priceErrors = validateStep3EquipmentPrices();
        if (Object.keys(priceErrors).length > 0) {
          setEquipmentPriceErrors(priceErrors);
          return;
        }
        setEquipmentPriceErrors({});
        setCompletedSteps((prev) => new Set(prev).add(3));
        setStep(4);
        setMaxReachedStep((prev) => Math.max(prev, 4) as 1 | 2 | 3 | 4);
        return;
      }

      // Step 4 "Create Plan": validate discount tiers, then create the plan.
      // Per-category discount is not resolved here — it's applied later, per
      // client, from the discount ladder (discountTiers) once the client's
      // actual equipment is known and ranked by price.
      const tierErrors = validateStep4DiscountTiers();
      if (Object.keys(tierErrors).length > 0) {
        setDiscountTierErrors(tierErrors);
        return;
      }
      setDiscountTierErrors({});
      setCompletedSteps((prev) => new Set(prev).add(4));

      // First click: validation passed → go to "preview" step (button becomes "Create Plan")
      if (!isPreviewStep) {
        setIsPreviewStep(true);
        return;
      }

      // Second click (from preview): actually creates the plan
      setIsSubmitting(true);
      setErrors({});

      try {
        const input: CreateSubscriptionPlanInput = {
          planName: formData.planName.trim(),
          planDescription: formData.description.trim(),
          durationType,
          period,
          cycle,
          price: formData.price,
          services: services.map((entry) => {
            const service = {
              serviceTypeId: entry.serviceTypeId,
              usageLimit: entry.usageLimit,
              basePrice: entry.basePrice,
            };
            if (entry.discountPercentage) {
              return {
                ...service,
                discountPercentage: entry.discountPercentage,
              };
            }
            return service;
          }),
          equipmentCategories: equipmentCategoryOptions.map((category) => ({
            equipmentCategoryId: category.id,
            basePrice: equipmentPrices[category.id] ?? '',
          })),
          discountTiers: Array.from(
            { length: EQUIPMENT_RANK_COUNT },
            (_, i) => ({
              equipmentRank: i + 1,
              discountPercentage: discountTiers[i + 1] ?? '0',
            })
          ),
        };
        if (formData.userCount) {
          input.userCount = formData.userCount;
        }
        if (formData.facilityCount) {
          input.facilityCount = formData.facilityCount;
        }
        if (formData.qmsDocCount) {
          input.qmsDocCount = formData.qmsDocCount;
        }
        if (offerId) {
          input.offerId = offerId;
        }

        const result = await createSubscriptionPlan(input);

        if (result.success) {
          navigate('/Plany', {
            replace: true,
            state: { showCreatePlanSuccess: true },
          });
          return;
        }

        if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...result.fieldErrors }));
          // The preview screen is a read-only summary — it can't show an
          // inline field error, so drop back to the editable stepper.
          setIsPreviewStep(false);
        }
        const isPlanNameExistsError =
          result.error === ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS ||
          result.error
            ?.toLowerCase()
            .includes('subscription plan name already exists');
        if (isPlanNameExistsError) {
          setErrors((prev) => ({
            ...prev,
            planName: SUBSCRIPTION_ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS,
          }));
          setIsPreviewStep(false);
          setStep(1);
        } else if (result.error) {
          setSnackbarError(result.error);
          setIsSnackbarOpen(true);
        }
      } catch {
        setSnackbarError(ERROR_MESSAGES.UNEXPECTED_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      step,
      isPreviewStep,
      validateStep1,
      validateStep2Services,
      validateStep3EquipmentPrices,
      validateStep4DiscountTiers,
      services,
      formData,
      durationType,
      period,
      cycle,
      offerId,
      navigate,
      equipmentCategoryOptions,
      equipmentPrices,
      discountTiers,
    ]
  );

  const hasContent = Boolean(
    formData.planName ||
      formData.description ||
      formData.price ||
      formData.userCount ||
      formData.facilityCount ||
      formData.qmsDocCount ||
      durationType ||
      period ||
      cycle ||
      offerId ||
      services.length > 0 ||
      Object.values(equipmentPrices).some((price) => price.trim() !== '')
  );

  // Cancel always means leave the whole flow, regardless of step — it
  // either exits outright (nothing entered yet) or asks for confirmation.
  // Going back a step is a separate "Back" button, which reuses
  // handleStepTabClick since backward nav needs no validation.
  const handleCancel = useCallback(() => {
    if (!hasContent) {
      navigate('/Plany', { replace: true });
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasContent, navigate]);

  // Only a step at or below maxReachedStep is ever clickable (see maxStep
  // passed to StepTabNav), so a click never needs re-validation here — it
  // already passed when maxReachedStep advanced past it, and completedSteps
  // was already updated in handleSubmit at that same point.
  const handleStepTabClick = useCallback((targetStep: number) => {
    setStep(targetStep as 1 | 2 | 3 | 4);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate('/Plany', { replace: true });
  }, [navigate]);

  const handleBackToEdit = useCallback(() => {
    setIsPreviewStep(false);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  return {
    // Step state
    step,
    maxReachedStep,
    completedSteps,

    // Step 1 form state
    formData,
    setFormData,
    durationType,
    setDurationType,
    period,
    setPeriod,
    cycle,
    setCycle,
    cycleOptions,
    errors,
    setErrors,
    offerId,
    setOfferId,
    offerDiscount,
    setOfferDiscount,
    offerName,
    setOfferName,

    // Step 2 (Services) state
    services,
    expandedServiceKey,
    serviceFieldErrors,
    servicesError,
    hasIncompleteService,
    removeDialogEntry,
    handleAddService,
    handleServiceTypeChange,
    handleServiceFieldChange,
    handleToggleExpandService,
    handleRequestRemoveService,
    handleCloseRemoveDialog,
    handleConfirmRemoveService,

    // Step 3 (Equipment Pricing) state
    equipmentCategoryOptions,
    equipmentCategoriesLoading,
    equipmentPrices,
    equipmentPriceErrors,
    handleEquipmentPriceChange,

    // Step 4 (Discount Logic) state
    discountTiers,
    discountTierErrors,
    handleDiscountTierChange,

    // UI state
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    isPreviewStep,
    previewData,

    // Handlers
    handleSubmit,
    handleCancel,
    handleStepTabClick,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    handleBackToEdit,

    // Helpers (for form JSX)
    formatPriceWithSpaces,
    getBillingDescription,
    cycleToEnglish,
    periodToEnglish,
  };
}
